import React, { useState, useEffect, useMemo } from "react";
import * as AppConstants from "AppConstants";

// import dayjs from "dayjs";
import {
  checkUserHasManagerRole,
  readSessionCurrentManager,
  getCurrentManager,
} from "app/services/app.state.service";
import { Card, Grid } from "@mui/material";
import { HolidayListView } from "app/components/leave-plans/HolidayListView";
import { ActivityStatsDataGridBase } from "app/components/dashboards/ActivityStatsDataGridBase";
import { loadUnifiedEffortsMonthly } from "app/services/report.service";
import { loadMembersFromBackendForManager, loadScopeManagersForIds } from "app/services/manager.service";
import {
  // getCurrentMonthStartDate,
  // getCurrentYearStartDate,
  getDateInDDMMYYYYFormat,
} from "utils/dateUtil";
import { mergeManagerScope, deriveSubtreeL1Options } from "utils/managerSubtree";
import { handleAxiosError } from "app/services/app.error.handler";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { ActivityStatsChartsRow } from "app/components/dashboards/ActivityStatsChartsRow";
import { LeavePlanSummaryCard } from "app/components/leave-plans/LeavePlanSummaryCard";
import { ConsolidatedReportFilter } from "app/components/reports/ConsolidatedReportFilter";
import { useDispatch, useSelector } from "react-redux";
import { dashboardActions } from "store/dashboard-slice";
import { clientActions } from "store/client-slice";
import { NoDataBanner } from "app/components/dashboards/NoDataBanner";
import { EffortFilterBar } from "app/components/common/EffortFilterBar";
import { defaultPeriod, periodRange } from "app/components/common/PeriodFilter";
import { useManagerScopeOptions } from "app/hooks/useManagerScopeOptions";
import { useUserFilterOptions } from "app/hooks/useUserFilterOptions";
import { resolveOwnerParams } from "utils/effortOwnerParams";
import "./dashboards.css";

// Stable empty-object fallback so the membersByClient selector never returns a
// fresh reference (which would trigger needless re-renders) before the cache exists.
const EMPTY_CACHE = {};

export const ManagerDashboard = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const activityStatsData = useSelector((state) => state.dashboard.rows);
  // Per-client member cache warmed on dashboard load (see report.prefetch.service).
  // For a manager it holds exactly this widget's team — the members reporting to
  // them, keyed by client id — so read it before hitting the network.
  const membersByClient = useSelector((state) => state.client?.membersByClient || EMPTY_CACHE);

  const [effortClientId, setEffortClientId] = useState(AppConstants.ALL_CLIENTS);
  const [managerClients, setManagerClients] = useState([]);
  const [selfManager, setSelfManager] = useState(null);
  // The logged-in manager's subtree (self + L1 children). An L1 gets just themselves;
  // an L2 also gets each L1 reporting to them, so the summary can roll up the L1s'
  // own hours and their team members alongside the L2's own team.
  const [subtreeManagers, setSubtreeManagers] = useState([]);

  const [pendingClientId, setPendingClientId] = useState(AppConstants.ALL_CLIENTS);
  const [pendingPeriod, setPendingPeriod] = useState(defaultPeriod);
  const [pendingUser, setPendingUser] = useState(AppConstants.ALL_USERS);
  const [appliedRange, setAppliedRange] = useState(() => periodRange(defaultPeriod()));
  const [appliedUser, setAppliedUser] = useState(AppConstants.ALL_USERS);
  const [teamMembers, setTeamMembers] = useState([]);
  const [scopeTeamMembers, setScopeTeamMembers] = useState([]);

  useEffect(() => {
    const list = readSessionCurrentManager();
    setManagerClients(Array.isArray(list) ? list : []);
    getCurrentManager()
      .then((m) => setSelfManager(Array.isArray(m) ? (m[0] || null) : (m || null)))
      .catch(() => setSelfManager(null));
  }, []);

  // Load the subtree from ALL the manager's records, not just the first — an L1 may
  // report through any of them. An L1 resolves to just themselves (behaviour unchanged).
  useEffect(() => {
    const managerIds = managerClients.map((c) => c.id).filter((id) => id != null);
    if (managerIds.length === 0) return;
    let ignore = false;
    loadScopeManagersForIds(managerIds)
      .then((list) => {
        if (!ignore) setSubtreeManagers(list);
      })
      .catch(() => {
        if (!ignore) setSubtreeManagers([]);
      });
    return () => {
      ignore = true;
    };
  }, [managerClients]);

  // Every client the manager touches and the L1s they can report on — both derived from
  // the subtree unioned with their own assignments, so a client or child reached only
  // through one of the two is still offered.
  const { clientOptions, subtreeL1Options } = useManagerScopeOptions({
    subtreeManagers,
    managerClients,
    selectedClient: pendingClientId,
  });

  // "(Me)" needs one of the manager's own records; any of them roots the same person.
  const selfManagerIds = managerClients.map((c) => c.id).filter((id) => id != null);

  // The leave card owns its own client filter and narrows these lists itself, so it needs the
  // UNNARROWED scope. subtreeL1Options is already scoped to the
  // effort bar's pending client — handing those over would intersect two independent client
  // selections and empty the leave card's Member / Manager dropdown whenever the two differed.
  // The effort bar's own view of the shared list.
  const effortTeamMembers = useMemo(
    () =>
      pendingClientId === AppConstants.ALL_CLIENTS
        ? scopeTeamMembers
        : scopeTeamMembers.filter(
            (m) => String(m.client?.id ?? m.clientId) === String(pendingClientId)
          ),
    [scopeTeamMembers, pendingClientId]
  );

  const leaveSubtreeL1s = useMemo(
    () =>
      deriveSubtreeL1Options(
        subtreeManagers,
        managerClients.map((c) => c.id).filter((id) => id != null),
        AppConstants.ALL_CLIENTS,
        AppConstants.ALL_CLIENTS
      ),
    [subtreeManagers, managerClients]
  );

  const { memberOptions, managerOptions, selfOption } = useUserFilterOptions({
    members: effortTeamMembers,
    managers: subtreeL1Options,
    self: selfManagerIds.length > 0 ? { id: selfManagerIds[0], name: selfManager?.name } : null,
  });

  const clientLabelById = useMemo(() => {
    const map = {};
    clientOptions.forEach((o) => { map[String(o.id)] = o.label; });
    return map;
  }, [clientOptions]);

  // The query is already scoped to the manager's subtree and the selected user.
  const displayRows = activityStatsData;

  const onClientChange = (val) => { setPendingClientId(val); setPendingUser(AppConstants.ALL_USERS); };
  const onApply = () => {
    setEffortClientId(pendingClientId);
    setAppliedRange(periodRange(pendingPeriod));
    setAppliedUser(pendingUser);
  };

  useEffect(() => {
    if (!checkUserHasManagerRole()) return;
    if (managerClients.length === 0) return;

    let ignore = false;

    // Scope is resolved server-side now: the query roots at the manager's own records and expands
    // the subtree in SQL. Previously this fetched every owner on each client — one request per
    // client — and then discarded rows for people outside the team, which meant a manager's
    // browser received members who do not report to them.
    setIsLoading(true);
    loadUnifiedEffortsMonthly({
      fromDate: appliedRange.from,
      toDate: appliedRange.to,
      managerIds: managerClients.map((c) => c.id).filter((id) => id != null),
      clientIds: effortClientId === AppConstants.ALL_CLIENTS ? null : [effortClientId],
      ...resolveOwnerParams(appliedUser, teamMembers, selfManagerIds, subtreeManagers),
    })
      .then((response) => {
        if (ignore) return;
        setIsLoading(false);
        dispatch(dashboardActions.replaceData(
          response.status === 204 || !Array.isArray(response.data) ? [] : response.data
        ));
      })
      .catch((error) => {
        if (ignore) return;
        setIsLoading(false);
        dispatch(dashboardActions.replaceData([]));
        handleAxiosError(error);
      });

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedRange, appliedUser, effortClientId, managerClients, dispatch]);

  // Team membership is still loaded, but only to populate the User dropdown and to resolve a
  // selected userId to member ids — no longer to filter the response.
  useEffect(() => {
    const teamAccumulator = [];
    if (!checkUserHasManagerRole()) return;
    if (managerClients.length === 0) return;

    let ignore = false;

    // Full scope = the manager's own client assignments unioned with their subtree
    // (self + L1 children). For an L1 this is just themselves; for an L2 it adds each
    // L1 child so their teams and own hours roll into the summary.
    const scope = mergeManagerScope(subtreeManagers, managerClients);

    const targetClientIds =
      effortClientId === AppConstants.ALL_CLIENTS
        ? [...new Set(scope.map((c) => String(c.clientId)))]
        : [effortClientId];


    Promise.all(
      targetClientIds.map((clientId) => {
        // Managers in scope that run this client — the L2 and any L1 child on it.
        const managersOnClient = scope.filter((c) => String(c.clientId) === String(clientId));

        const fetchTeam = (mgrId) =>
          loadMembersFromBackendForManager(mgrId)
            .then((response) => (response.status === 204 ? [] : response?.data ?? []))
            .catch(() => []);

        // Each in-scope manager's team on this client. With exactly one manager, prefer the
        // cache warmed on dashboard load (and warm it on a miss); with several (the L2 and an
        // L1 both on this client) fetch each, since the per-client cache holds only one
        // manager's members and can't represent the whole set.
        if (managersOnClient.length === 0) {
          return Promise.resolve([]);
        }
        if (managersOnClient.length === 1) {
          const cachedTeam = membersByClient[String(clientId)];
          return cachedTeam
            ? Promise.resolve(cachedTeam)
            : fetchTeam(managersOnClient[0].id).then((team) => {
                dispatch(clientActions.cacheMembersForClient({ clientId, data: team }));
                return team;
              });
        }
        return Promise.all(managersOnClient.map((m) => fetchTeam(m.id))).then((lists) => lists.flat());
      })
    ).then((lists) => {
      if (ignore) return;
      teamAccumulator.push(...lists.flat());
      setTeamMembers(teamAccumulator);
    });

    return () => {
      ignore = true;
    };
    // membersByClient is read but intentionally omitted: it's a shared cache the
    // child ConsolidatedReportFilter also writes to, and re-running this stats load
    // whenever that cache changes would undo the very network savings it provides.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedRange, effortClientId, managerClients, subtreeManagers, dispatch]);

  // The team behind both filter bars' Member dropdowns. Mirrors the Consolidated Report's
  // cache-aware load: reuse the warmed per-client cache and only fetch on a miss.
  useEffect(() => {
    // The WHOLE scope, every client — not the pending client. Two filter bars read this list now
    // (the effort bar and the leave card) and each narrows it against its own client selection, so
    // scoping the fetch to one bar's selection would empty the other's dropdown. Loading once per
    // subtree also means changing a client no longer refetches.
    const managersInScope = mergeManagerScope(subtreeManagers, managerClients);
    if (managersInScope.length === 0) {
      setScopeTeamMembers([]);
      return;
    }
    let ignore = false;
    const fetchTeam = (m) =>
      loadMembersFromBackendForManager(m.id)
        .then((r) => (r.status === 204 || !Array.isArray(r.data) ? [] : r.data))
        .catch(() => []);
    // Group by client: a client with one in-scope manager can reuse the warmed
    // per-client cache; several managers on a client must each be fetched (the cache
    // holds only one manager's members and can't represent the whole set).
    const byClient = managersInScope.reduce((acc, m) => {
      (acc[m.clientId] = acc[m.clientId] || []).push(m);
      return acc;
    }, {});
    // Stamp the client each team was fetched for. The per-manager endpoint returns member records
    // with no client field, so provenance is the ONLY thing that says which client a member was
    // reached through — and both filter bars narrow this list by client. Without the stamp,
    // choosing any client empties the Member dropdown.
    const tagWithClient = (team, clientId) =>
      (Array.isArray(team) ? team : []).map((m) =>
        m?.clientId == null && m?.client?.id == null ? { ...m, clientId: Number(clientId) } : m
      );
    Promise.all(
      Object.entries(byClient).flatMap(([clientId, mgrs]) => {
        if (mgrs.length === 1) {
          const cached = membersByClient[String(clientId)];
          return [
            cached
              ? Promise.resolve(tagWithClient(cached, clientId))
              : fetchTeam(mgrs[0]).then((team) => {
                  dispatch(clientActions.cacheMembersForClient({ clientId, data: team }));
                  return tagWithClient(team, clientId);
                }),
          ];
        }
        return mgrs.map((m) => fetchTeam(m).then((team) => tagWithClient(team, clientId)));
      })
    ).then((lists) => {
      // Not deduped: a member reached through two clients needs one record per client so that
      // narrowing to either still finds them. buildUserOptions dedupes by person for display.
      if (!ignore) setScopeTeamMembers(lists.flat());
    });
    return () => { ignore = true; };
  }, [subtreeManagers, managerClients, membersByClient, dispatch]);

  if (!checkUserHasManagerRole()) {
    return <div>You do not have access to this functionality.</div>;
  }

  return (
    <div className="content-container dashboard-container">
      <Grid container rowSpacing={10} columnSpacing={0}>
        <Grid size={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={1}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                {`Efforts summary for period - ${getDateInDDMMYYYYFormat(
                  appliedRange.from
                )} and ${getDateInDDMMYYYYFormat(appliedRange.to)}`}
              </MDTypography>
            </MDBox>
            <MDBox px={2} pt={3}>
              <EffortFilterBar
                idPrefix="manager-effort"
                showClient
                clientValue={pendingClientId}
                onClientChange={onClientChange}
                clientOptions={clientOptions}
                showMemberManagerDropdown
                userValue={pendingUser}
                onUserChange={setPendingUser}
                memberOptions={memberOptions}
                managerOptions={managerOptions}
                selfOption={selfOption}
                period={pendingPeriod}
                onPeriodChange={setPendingPeriod}
                onAction={onApply}
              />
            </MDBox>
            {isLoading ? (
              <NoDataBanner message="Loading efforts summary..." />
            ) : displayRows.length === 0 ? (
              <NoDataBanner />
            ) : (
              <>
                <ActivityStatsChartsRow
                  activityStatsData={displayRows}
                  fromDate={appliedRange.from}
                  toDate={appliedRange.to}
                />
                <MDBox mt={2}>
                  <ActivityStatsDataGridBase activityStatsData={displayRows} clientLabelById={clientLabelById} />
                </MDBox>
              </>
            )}
          </Card>
        </Grid>

        <Grid size={12}>
          <ConsolidatedReportFilter role={AppConstants.ROLE_EMPLOYEE_MANAGER} members={[]} />
        </Grid>
        <Grid size={12}>
          {/* Rooted at EVERY manager record the person holds, matching the effort card above: a
              single record hid the L1s reporting through another one. */}
          <LeavePlanSummaryCard
            idPrefix="manager-leave"
            role={AppConstants.ROLE_EMPLOYEE_MANAGER}
            context={{ managerIds: selfManagerIds }}
            showClient
            clientOptions={clientOptions}
            showUser
            members={scopeTeamMembers}
            managers={leaveSubtreeL1s}
            self={selfManagerIds.length > 0 ? { id: selfManagerIds[0], name: selfManager?.name } : null}
            selfManagerIds={selfManagerIds}
          />
        </Grid>
        <Grid size={12}>
          <HolidayListView />
        </Grid>
      </Grid>
    </div>
  );
};
